import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, XIcon } from './icons';
import { hexToHsl, hslToHex } from '../lib/utils';
import { useTranslation } from '../i18n/useTranslation';

interface ColorPickerPanelProps {
  onColorChange: (color: string) => void;
  currentColor: string;
  disabled: boolean;
  isVisible: boolean;
  title: string;
  onClose: () => void;
}

const predefinedColors = [
  { nameKey: 'color_white', hex: '#FFFFFF' },
  { nameKey: 'color_black', hex: '#000000' },
  { nameKey: 'color_red', hex: '#FF0000' },
  { nameKey: 'color_blue', hex: '#0000FF' },
  { nameKey: 'color_green', hex: '#008000' },
  { nameKey: 'color_yellow', hex: '#FFFF00' },
  { nameKey: 'color_gray', hex: '#808080' },
  { nameKey: 'color_pink', hex: '#FFC0CB' },
  { nameKey: 'color_purple', hex: '#800080' },
  { nameKey: 'color_orange', hex: '#FFA500' },
  { nameKey: 'color_brown', hex: '#A52A2A' },
];

const LOCAL_STORAGE_RECENT_COLORS_KEY = 'virtual_try_on_recent_colors';

const ColorPickerPanel: React.FC<ColorPickerPanelProps> = ({ onColorChange, currentColor, disabled, isVisible, title, onClose }) => {
  const { t } = useTranslation();
  const [hsl, setHsl] = useState(() => hexToHsl(currentColor));
  const [localHex, setLocalHex] = useState(currentColor);
  const [recentlyUsedColors, setRecentlyUsedColors] = useState<string[]>(() => {
    try {
      const storedColors = localStorage.getItem(LOCAL_STORAGE_RECENT_COLORS_KEY);
      return storedColors ? JSON.parse(storedColors) : [];
    } catch (e) {
      console.error("Failed to load recently used colors from localStorage", e);
      return [];
    }
  });

  // Update HSL state when currentColor prop changes (e.g., loading new outfit)
  useEffect(() => {
    const newHsl = hexToHsl(currentColor);
    setHsl(newHsl);
    setLocalHex(currentColor);
  }, [currentColor]);

  // Update localHex whenever HSL values change
  useEffect(() => {
    setLocalHex(hslToHex(hsl.h, hsl.s, hsl.l));
  }, [hsl]);

  // Persist recently used colors to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_RECENT_COLORS_KEY, JSON.stringify(recentlyUsedColors));
    } catch (e) {
      console.error("Failed to save recently used colors to localStorage", e);
    }
  }, [recentlyUsedColors]);

  const addRecentlyUsedColor = useCallback((colorHex: string) => {
    setRecentlyUsedColors(prevColors => {
      // Remove if already exists to move it to the front
      const filteredColors = prevColors.filter(c => c !== colorHex);
      // Add to the beginning, limit to 5
      return [colorHex, ...filteredColors].slice(0, 5);
    });
  }, []);

  const handleSliderChange = useCallback((type: 'h' | 's' | 'l', value: number) => {
    setHsl(prevHsl => ({ ...prevHsl, [type]: value }));
  }, []);

  const handlePredefinedColorSelect = useCallback((hex: string) => {
    const newHsl = hexToHsl(hex);
    setHsl(newHsl);
    setLocalHex(hex);
  }, []);

  const handleApply = useCallback(() => {
    if (localHex !== currentColor) {
      onColorChange(localHex);
      addRecentlyUsedColor(localHex);
    }
  }, [localHex, currentColor, onColorChange, addRecentlyUsedColor]);

  const getCheckCircleColor = useCallback((hex: string) => {
    // Determine text color for CheckCircleIcon based on background lightness
    const { l } = hexToHsl(hex);
    // Use black for light backgrounds, white for dark backgrounds
    return l > 50 ? '#000000' : '#FFFFFF';
  }, []);

  if (!isVisible) return null; // Only render if isVisible is true

  return (
    <div className="pt-6 border-t border-gray-400/50 dark:border-gray-500/50">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-serif tracking-wider text-gray-800 dark:text-gray-100 truncate pr-2">{title}</h2>
        <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
            aria-label={t('shareOutfitModal_close')}
        >
            <XIcon className="w-5 h-5" />
        </button>
      </div>
      
      {recentlyUsedColors.length > 0 && (
        <div className="mb-4">
          <h3 className="text-base font-semibold text-gray-700 mb-2 dark:text-gray-200">{t('canvas_colorPicker_recentlyUsed')}</h3>
          <div className="flex flex-wrap gap-2">
            {recentlyUsedColors.map((hex) => (
              <button
                key={hex}
                onClick={() => handlePredefinedColorSelect(hex)}
                className={`w-8 h-8 rounded-full border-2 focus:outline-none focus:ring-2 focus:ring-offset-2 ${localHex === hex ? 'ring-gray-800 border-gray-900 dark:ring-indigo-500 dark:border-indigo-600' : 'border-gray-300 hover:border-gray-500 dark:border-gray-600 dark:hover:border-gray-500'}`}
                style={{ backgroundColor: hex }}
                disabled={disabled}
                aria-label={`${t('canvas_colorPicker_selectRecent')} ${hex}`}
              >
                {localHex === hex && (
                  <CheckCircleIcon className="w-full h-full text-white drop-shadow-md" style={{ color: getCheckCircleColor(hex) }} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <h3 className="text-base font-semibold text-gray-700 mb-2 dark:text-gray-200">{t('canvas_colorPicker_predefinedColors')}</h3>
      <div className="flex flex-wrap gap-2 mb-4">
        {predefinedColors.map((color) => (
          <button
            key={color.hex}
            onClick={() => handlePredefinedColorSelect(color.hex)}
            className={`w-8 h-8 rounded-full border-2 focus:outline-none focus:ring-2 focus:ring-offset-2 ${localHex === color.hex ? 'ring-gray-800 border-gray-900 dark:ring-indigo-500 dark:border-indigo-600' : 'border-gray-300 hover:border-gray-500 dark:border-gray-600 dark:hover:border-gray-500'}`}
            style={{ backgroundColor: color.hex }}
            disabled={disabled}
            aria-label={`${t('canvas_colorPicker_select')} ${t(color.nameKey)} ${t('canvas_colorPicker_color')}`}
          >
            {localHex === color.hex && (
              <CheckCircleIcon className="w-full h-full text-white drop-shadow-md" style={{ color: getCheckCircleColor(color.hex) }} />
            )}
          </button>
        ))}
        <input
          type="color"
          value={localHex}
          onChange={(e) => {
            const newHex = e.target.value;
            const newHsl = hexToHsl(newHex);
            setHsl(newHsl);
            setLocalHex(newHex);
          }}
          className="w-8 h-8 rounded-full border-2 border-gray-300 cursor-pointer disabled:opacity-50 dark:border-gray-600"
          disabled={disabled}
          aria-label={t('canvas_colorPicker_customColorPicker')}
        />
      </div>

      {/* HSL Sliders */}
      <div className="space-y-4 mb-6">
        <div>
          <label htmlFor="hue-slider" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('canvas_colorPicker_hue')}</label>
          <input
            id="hue-slider"
            type="range"
            min="0"
            max="360"
            value={hsl.h}
            onChange={(e) => handleSliderChange('h', parseFloat(e.target.value))}
            disabled={disabled}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer range-slider-hue disabled:opacity-50"
            style={{ background: `linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)` }}
          />
        </div>
        <div>
          <label htmlFor="saturation-slider" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('canvas_colorPicker_saturation')}</label>
          <input
            id="saturation-slider"
            type="range"
            min="0"
            max="100"
            value={hsl.s}
            onChange={(e) => handleSliderChange('s', parseFloat(e.target.value))}
            disabled={disabled}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer range-slider-saturation disabled:opacity-50"
            style={{ background: `linear-gradient(to right, hsl(${hsl.h}, 0%, ${hsl.l}%), hsl(${hsl.h}, 100%, ${hsl.l}%))` }}
          />
        </div>
        <div>
          <label htmlFor="lightness-slider" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('canvas_colorPicker_lightness')}</label>
          <input
            id="lightness-slider"
            type="range"
            min="0"
            max="100"
            value={hsl.l}
            onChange={(e) => handleSliderChange('l', parseFloat(e.target.value))}
            disabled={disabled}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer range-slider-lightness disabled:opacity-50"
            style={{ background: `linear-gradient(to right, #000 0%, hsl(${hsl.h}, ${hsl.s}%, 50%), #fff 100%)` }}
          />
        </div>
      </div>

      <button
        onClick={handleApply}
        disabled={disabled || localHex === currentColor}
        className="w-full flex items-center justify-center text-center bg-gray-900 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 ease-in-out hover:bg-gray-700 active:scale-95 text-base disabled:opacity-50 disabled:cursor-not-allowed dark:bg-indigo-600 dark:hover:bg-indigo-500"
      >
        {t('canvas_colorPicker_applyColor')}
      </button>
    </div>
  );
};

export default ColorPickerPanel;